import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { socialImageMeta, SITE_URL } from "@/lib/social";
import { makeSeoDescription } from "@/lib/seo-description";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";

interface ListedArticle {
  title: string;
  slug: string;
  country: string;
  category: string;
  excerpt: string;
  published_at: string;
}

const fetchLatest = createServerFn({ method: "GET" }).handler(async (): Promise<ListedArticle[]> => {
  const db = supabase as unknown as SupabaseClient;
  const { data } = await db
    .from("articles")
    .select("title, slug, country, category, excerpt, published_at")
    .eq("published", true)
    .order("published_at", { ascending: false })
    .limit(50);
  return (data as ListedArticle[] | null) ?? [];
});

export const Route = createFileRoute("/latest")({
  loader: () => fetchLatest(),
  head: () => ({
    meta: [
      { title: "Latest Expat Updates 2026 | LifeWestWay" },
      {
        name: "description",
        content:
          makeSeoDescription("Newly published LifeWestWay guides on visas, jobs, tax, housing and finance across the USA, UK, Canada, Australia, Italy and Portugal."),
      },
      { property: "og:title", content: "Latest Expat Updates 2026 | LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("Newly published guides on visas, jobs, tax, housing and finance for six Western destinations."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://lifewestway.com/latest" },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/latest" }],
  }),
  component: LatestPage,
});

function LatestPage() {
  const posts = Route.useLoaderData() as ListedArticle[];

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-12">
      <h1 className="font-display text-4xl font-bold text-foreground">Latest updates</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Fresh guidance from the LifeWestWay newsroom — published as rules, thresholds and costs
        change across our six destination countries.
      </p>

      <section className="mt-8 space-y-4 text-[15px] leading-7 text-foreground/80">
        <h2 className="font-display text-2xl font-bold text-accent">What this page is for</h2>
        <p>
          Immigration, tax and cost-of-living rules do not stand still. In any given year the USA
          revises H-1B fees and marketplace health subsidies, the UK moves salary thresholds and the
          Immigration Health Surcharge, Australia reprices visa categories and adjusts points
          settings, Canada publishes a new multi-year immigration levels plan, and Italy and Portugal
          amend their tax regimes for new residents. A guide that was accurate last spring can quietly
          become misleading by autumn. This page exists so you can see, in one place, what we have
          published or revised most recently rather than hunting through individual country silos.
        </p>
        <h2 className="font-display text-2xl font-bold text-accent">How to use it</h2>
        <p>
          Each entry below shows the destination country, the category it belongs to, a short summary
          and its publication date. If you are in the middle of an application, scan the entries for
          your country first: a change to a fee, a threshold or a processing route is the kind of
          detail that changes your timeline or your budget. If you are still deciding where to move,
          read across countries instead — comparing how two destinations treat the same issue, such as
          healthcare access in the first year or tax on foreign income, is usually more useful than
          reading a single country end to end.
        </p>
        <h2 className="font-display text-2xl font-bold text-accent">How we decide what to publish</h2>
        <p>
          We publish when something genuinely changes for a person moving abroad — a new fee schedule,
          a closed or reopened visa route, a revised income requirement, a new tax rule for new
          residents, or a shift in rental and healthcare costs large enough to alter a realistic
          budget. We do not republish press releases, and we do not post daily news for its own sake.
          Every update is traced back to the responsible official body — USCIS and the IRS, the UK
          Home Office and HMRC, the Department of Home Affairs and the ATO, IRCC and the CRA, the
          Agenzia delle Entrate and Questura offices in Italy, and AIMA and the Autoridade Tributária
          in Portugal — before it appears here.
        </p>
        <p>
          Dates shown are publication dates for that revision, so an older date does not mean the
          guidance is stale; it means nothing material has changed since. Where a figure is expected
          to move within the year, the underlying guide says so explicitly and tells you which office
          to check before you act.
        </p>
        <h2 className="font-display text-2xl font-bold text-accent">What tends to change, and when</h2>
        <p>
          Updates cluster around predictable moments in the year. Fee schedules and health
          surcharges are usually revised at the start of a financial year, which falls in different
          months depending on the country — April in the UK, July in Australia, January for most US
          federal fees. Immigration quotas and points settings tend to follow national budgets or
          annual levels plans, while tax rules for new residents change with each finance act.
          Rental and childcare costs move continuously rather than on a schedule, so we re-check
          those figures against national statistics offices rather than listings sites, which
          overstate asking prices in the largest cities.
        </p>
        <p>
          If you are mid-application, the safest habit is to check the entry for your country here
          before you pay any fee or book any appointment, then confirm the number on the official
          page we link to. Fees are the single most common thing to move quietly, and an
          underpayment is usually treated as an invalid application rather than a shortfall you can
          top up. The same applies to income thresholds: an offer that met the requirement when it
          was drafted may sit under a revised floor by the time the visa is filed.
        </p>
        <p>
          Corrections belong here too. When we get something wrong — a misread threshold, a route
          we described as open after it closed, a figure quoted in the wrong currency — we amend the
          guide, note what changed, and re-date it rather than silently editing the page. If you
          spot an error before we do, tell us; readers in the middle of an application are usually
          faster than any editorial calendar, and those messages are the reason several guides on
          this site are more accurate than they would otherwise be.
        </p>
      </section>



      {posts.length === 0 ? (
        <p className="mt-10 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No newsroom posts yet. Check back shortly.
        </p>
      ) : (
        <ul className="mt-10 space-y-6">
          {posts.map((p) => (
            <li key={p.slug} className="rounded-xl border border-border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {p.country.toUpperCase()} · {p.category}
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-foreground">
                <Link title={`Read the latest update: ${p.title}`} to="/post/$slug" params={{ slug: p.slug }} className="hover:underline">
                  {p.title}
                </Link>
              </h2>
              {p.excerpt ? <p className="mt-2 text-muted-foreground">{p.excerpt}</p> : null}
              <p className="mt-3 text-xs text-muted-foreground">
                {new Date(p.published_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </li>
          ))}
        </ul>
      )}

      <ArticleDisclaimer />
    </div>
  );
}
